import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';

export type CustomerButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type CustomerButtonSize = 'sm' | 'md' | 'lg';

interface CustomerButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: CustomerButtonVariant;
  size?: CustomerButtonSize;
  /** Shows an inline spinner, sets aria-busy and blocks interaction. */
  loading?: boolean;
  /** Active-voice label shown while loading (UI_UX_STANDARDS: "Saving…"). */
  loadingLabel?: ReactNode;
  icon?: IconDefinition;
  iconPosition?: 'start' | 'end';
  fullWidth?: boolean;
}

const VARIANT: Record<CustomerButtonVariant, string> = {
  primary:
    'bg-customer-primary text-customer-action-ink shadow-customer-sm hover:bg-customer-primary/90 disabled:hover:bg-customer-primary',
  secondary:
    'bg-customer-primary/10 text-customer-primary-text hover:bg-customer-primary/15 disabled:hover:bg-customer-primary/10',
  outline:
    'border border-customer-border bg-customer-surface text-customer-text hover:border-customer-text/30 hover:bg-customer-surface-muted disabled:hover:bg-customer-surface',
  ghost: 'text-customer-text/80 hover:bg-customer-text/[0.06] hover:text-customer-text',
  danger:
    'border border-customer-danger/30 bg-customer-surface text-customer-danger-text hover:bg-customer-danger/[0.08]',
};

/* Every size meets the 44px touch minimum on touch devices; `sm` relaxes to
   40px only from `sm:` upwards where a pointer is more likely. */
const SIZE: Record<CustomerButtonSize, string> = {
  sm: 'min-h-11 sm:min-h-10 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-sm gap-2',
  lg: 'min-h-12 px-5 text-base gap-2',
};

/**
 * Customer Portal button — the single button primitive for customer screens.
 * Radius 8px (standards cap), tokens only, visible focus, pressed feedback on
 * touch, loading + disabled states carried in one place instead of in every
 * call site's className.
 */
export const CustomerButton = forwardRef<HTMLButtonElement, CustomerButtonProps>(
  function CustomerButton(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingLabel,
      icon,
      iconPosition = 'start',
      fullWidth = false,
      className,
      children,
      disabled,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    const isDisabled = disabled || loading;
    const iconEl = loading ? (
      <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
    ) : icon ? (
      <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" aria-hidden="true" />
    ) : null;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        className={cn(
          'customer-pressable inline-flex select-none items-center justify-center whitespace-nowrap rounded-customer-sm font-medium leading-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface',
          'disabled:cursor-not-allowed disabled:opacity-50',
          VARIANT[variant],
          SIZE[size],
          fullWidth && 'w-full',
          className,
        )}
        {...rest}
      >
        {iconPosition === 'start' && iconEl}
        <span className="truncate">{loading && loadingLabel ? loadingLabel : children}</span>
        {iconPosition === 'end' && iconEl}
      </button>
    );
  },
);
